import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement, PageChangeEvent } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { IPopup } from 'app/shared/elements/popup-base';
import { deepClone } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import * as FM from 'app/fm/models/essette-api';
import { FaxService } from 'app/fm/services/fax-service';


@autoinject()
export class ManageFaxAccountsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected faxAccounts: ReadonlyArray<FM.FaxAccount> = [];
    protected editedFaxAccount: FM.FaxAccount = {};

    protected validate = validate;

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;
    protected includeInactive = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: FaxService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        if(sorting.entries.length === 0) {
            this.sorting.entries = [
                {
                    reference: 'description',
                    direction: SortDirection.Ascending
                }
            ];
        }

        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadFaxAccounts();
    }

    protected saveFaxAccount(faxAccount: FM.FaxAccount): void {
        this.editedFaxAccount = deepClone(faxAccount);
        this.$modal.open();
    }

    protected closeModal(): void {
        if(this.$form.reset) this.$form.reset();
        this.$modal.close();
    }

    protected async handleFormSuccess(): Promise<void> {
        this.closeModal();
        await this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadFaxAccounts(): Promise<void> {
        const includes: string[] = [
            'queue'
        ];

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadFaxAccounts(options, includes);
            this.faxAccounts = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateFaxAccount(faxAccount: FM.FaxAccount): Promise<void> {
        if(!faxAccount.id) return;

        // Show confirmation dialog only if deactivating
        if(faxAccount.isActive) {
            const result = await confirmation.showDeactivate('fm:faxAccount.$long', 1, false);
            if(result.dialogResponse !== confirmation.options.OK) { return; }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateFaxAccount(faxAccount.id, !faxAccount.isActive);
            isSuccessful = response.isSuccessful;
            await this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('fm:faxAccount.$long', !faxAccount.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
